import { Agenda, AgendaItemWithTimes, TrackMode } from '@livekatsomo/models';
import { addMilliseconds, addMinutes } from 'date-fns';
import { useMemo } from 'react';

/**
 * Returns an array of `AgendaItemWithTimes` objects, which are `AgendaItem` objects with added `startTime`, `endTime`, and `disabled` properties.
 * @param agendaItems An array of `AgendaItem` objects.
 * @param startDate The start date of the agenda.
 * @param trackStartTimeDelta The time delta for the start time of the track.
 * @param trackMode The mode of the track.
 * @param currentAgendaItemId The ID of the current agenda item.
 * @returns An array of `AgendaItemWithTimes` objects.
 */
export function useAgendaItemsWithStartTime(
  agendaItems: Agenda = [],
  startDate: Date,
  trackStartTimeDelta = 0,
  trackMode: TrackMode,
  currentAgendaItemId?: string,
): AgendaItemWithTimes[] {
  const agendaItemsWithDates = useMemo(
    () =>
      agendaItems.reduce((acc, agendaItem, index) => {
        const prevItem = index > 0 ? acc[acc.length - 1] : undefined;
        const startTime = prevItem
          ? addMinutes(prevItem.startTime, prevItem.duration)
          : addMilliseconds(startDate, trackStartTimeDelta || 0);
        const endTime = addMinutes(startTime, agendaItem.duration);
        const disabled =
          trackMode === 'live' &&
          currentAgendaItemId &&
          agendaItem.id !== currentAgendaItemId &&
          (prevItem?.disabled || index === 0);
        return [
          ...acc,
          {
            ...agendaItem,
            startTime,
            endTime,
            disabled,
          } as AgendaItemWithTimes,
        ];
      }, [] as Array<AgendaItemWithTimes>),
    [
      agendaItems,
      startDate,
      trackStartTimeDelta,
      trackMode,
      currentAgendaItemId,
    ],
  );
  return agendaItemsWithDates;
}
